import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReviewStep = ({ formData, onEdit }) => {
  const getAccessLevelLabel = (value) => {
    const labels = {
      public: 'Public Access',
      password: 'Password Protected',
      email: 'Email Verification',
      invite: 'Invite Only'
    };
    return labels?.[value] || value;
  };

  const getDownloadPermissionLabel = (value) => {
    const labels = {
      all: 'All Files',
      selected: 'Selected Only',
      none: 'No Downloads'
    };
    return labels?.[value] || value;
  };

  const getGalleryTypeLabel = (value) => {
    const labels = {
      photography: 'Photography Portfolio',
      videography: 'Videography Showcase',
      mixed: 'Mixed Media Gallery',
      event: 'Event Coverage'
    };
    return labels?.[value] || value;
  };

  const getTemplateLabel = (value) => {
    const labels = {
      grid: 'Grid Layout',
      masonry: 'Masonry Layout',
      slideshow: 'Slideshow Layout',
      timeline: 'Timeline Layout'
    };
    return labels?.[value] || value;
  };

  const InfoRow = ({ icon, label, value, onEditClick, stepNumber }) => (
    <div className="flex items-start justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-start gap-3 flex-1">
        <Icon name={icon} size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-xs md:text-sm text-muted-foreground">{label}</p>
          <p className="text-sm md:text-base font-medium mt-1 break-words">{value || 'Not set'}</p>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        iconName="Edit2"
        onClick={() => onEdit(stepNumber)}
        className="flex-shrink-0"
      />
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-semibold">Gallery Information</h3>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit2"
            iconPosition="left"
            onClick={() => onEdit(1)}
          >
            Edit
          </Button>
        </div>
        
        <div className="space-y-0">
          <InfoRow
            icon="Image"
            label="Gallery Name"
            value={formData?.galleryName}
            onEditClick={onEdit}
            stepNumber={1}
          />
          <InfoRow
            icon="FileText"
            label="Description"
            value={formData?.description}
            onEditClick={onEdit}
            stepNumber={1}
          />
          <InfoRow
            icon="Layers"
            label="Gallery Type"
            value={getGalleryTypeLabel(formData?.galleryType)}
            onEditClick={onEdit}
            stepNumber={1}
          />
          <InfoRow
            icon="Layout"
            label="Layout Template"
            value={getTemplateLabel(formData?.template)}
            onEditClick={onEdit}
            stepNumber={1}
          />
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-semibold">Client Details</h3>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit2"
            iconPosition="left"
            onClick={() => onEdit(1)}
          >
            Edit
          </Button>
        </div>
        
        <div className="space-y-0">
          <InfoRow
            icon="User"
            label="Client Name"
            value={formData?.clientName}
            onEditClick={onEdit}
            stepNumber={1}
          />
          <InfoRow
            icon="Mail"
            label="Client Email"
            value={formData?.clientEmail}
            onEditClick={onEdit}
            stepNumber={1}
          />
          <InfoRow
            icon="Phone"
            label="Client Phone"
            value={formData?.clientPhone}
            onEditClick={onEdit}
            stepNumber={1}
          />
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-semibold">Google Drive Folders</h3>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit2"
            iconPosition="left"
            onClick={() => onEdit(2)}
          >
            Edit
          </Button>
        </div>
        
        <div className="space-y-0">
          <InfoRow
            icon="FolderOpen"
            label="High Resolution Folder"
            value={formData?.selectedFolders?.highResName}
            onEditClick={onEdit}
            stepNumber={2}
          />
          <InfoRow
            icon="FolderOpen"
            label="Compressed Files Folder"
            value={formData?.selectedFolders?.compressedName}
            onEditClick={onEdit}
            stepNumber={2}
          />
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-semibold">Privacy & Settings</h3>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit2"
            iconPosition="left"
            onClick={() => onEdit(3)}
          >
            Edit
          </Button>
        </div>
        
        <div className="space-y-0">
          <InfoRow
            icon="Lock"
            label="Access Level"
            value={getAccessLevelLabel(formData?.accessLevel)}
            onEditClick={onEdit}
            stepNumber={3}
          />
          <InfoRow
            icon="Download"
            label="Download Permissions"
            value={getDownloadPermissionLabel(formData?.downloadPermission)}
            onEditClick={onEdit}
            stepNumber={3}
          />
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-sm font-medium mb-3">Enabled Features:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {formData?.requireRegistration && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Visitor Registration</span>
              </div>
            )}
            {formData?.trackActivity && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Activity Tracking</span>
              </div>
            )}
            {formData?.enableAnalytics && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Analytics Dashboard</span>
              </div>
            )}
            {formData?.sendNotifications && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Access Notifications</span>
              </div>
            )}
            {formData?.enableComments && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Comments</span>
              </div>
            )}
            {formData?.enableFavorites && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Favorites</span>
              </div>
            )}
            {formData?.autoSync && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Auto-Sync</span>
              </div>
            )}
            {formData?.enableWatermark && (
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <span>Watermark Protection</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-primary/10 border border-primary rounded-xl p-4 md:p-5">
        <div className="flex items-start gap-3">
          <Icon name="CheckCircle2" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm md:text-base font-medium mb-1">Ready to Create</p>
            <p className="text-xs md:text-sm text-muted-foreground">
              Review all settings above. Click "Create Gallery" to finalize and generate your shareable gallery link.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;